'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { TableSkeleton } from '../components/LoadingSkeleton';
import SearchBar from '../components/SearchBar';
import '../styles/Users.css';

function Users() {
  const { user: currentUser } = useAuth();
  const { success, error: showError } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    role: 'staff',
  });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.users);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to fetch users';
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(user => 
      user.username.toLowerCase().includes(term) ||
      user.name.toLowerCase().includes(term) ||
      user.role.toLowerCase().includes(term)
    );
  }, [users, searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      // Don't send empty password on update
      if (editingId && !submitData.password) {
        delete submitData.password;
      }

      if (editingId) {
        await usersAPI.update(editingId, submitData);
        success('User updated successfully');
      } else {
        // Password is required for new users
        if (!submitData.password) {
          submitData.password = 'password123'; // Default password
        }
        await usersAPI.create(submitData);
        success('User created successfully');
      }
      setFormData({
        username: '',
        password: '',
        name: '',
        role: 'staff',
      });
      setShowForm(false);
      setEditingId(null);
      fetchUsers();
    } catch (err) {
      const message = err.response?.data?.message || err.response?.data?.errors?.[0]?.message || 'Failed to save user';
      showError(message);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      password: '', // Don't populate password
      name: user.name,
      role: user.role,
    });
    setEditingId(user._id);
    setShowForm(true);
  };

  const handleDelete = async (id, username) => {
    if (window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      try {
        await usersAPI.delete(id);
        success('User deleted successfully');
        fetchUsers();
      } catch (err) {
        const message = err.response?.data?.message || 'Failed to delete user';
        showError(message);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      username: '',
      password: '',
      name: '',
      role: 'staff',
    });
  };

  return (
    <div className="users-container">
      <div className="page-header">
        <h1>User Management</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn">
          {showForm ? 'Cancel' : '+ New User'}
        </button>
      </div>

      {!loading && users.length > 0 && (
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by username, name, or role..."
        />
      )}

      {showForm && (
        <div className="form-container">
          <h2>{editingId ? 'Edit User' : 'Create New User'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  minLength="3"
                  maxLength="50"
                  placeholder="Choose a username"
                />
              </div>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Full name"
                />
              </div>
              <div className="form-group">
                <label>Password {editingId ? '(leave blank to keep current)' : '*'}</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required={!editingId}
                  minLength="6"
                  placeholder={editingId ? 'Enter new password (optional)' : 'Minimum 6 characters'}
                />
                {!editingId && (
                  <small>Default: password123 (user should change after first login)</small>
                )}
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                >
                  <option value="staff">Staff</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn">
                {editingId ? 'Update' : 'Create'} User
              </button>
              <button type="button" onClick={handleCancel} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : filteredUsers.length > 0 ? (
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Name</th>
                <th>Role</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user._id} className={user._id === currentUser?.id ? 'current-user' : ''}>
                  <td>{user.username}</td>
                  <td>{user.name}</td>
                  <td>
                    <span className={`role role-${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(user)}
                      className="btn btn-small"
                    >
                      Edit
                    </button>
                    {user._id !== currentUser?.id && (
                      <button
                        onClick={() => handleDelete(user._id, user.username)}
                        className="btn btn-danger btn-small"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : searchTerm ? (
        <div className="no-data">No users found matching "{searchTerm}"</div>
      ) : (
        <div className="no-data">No users found. Create your first user!</div>
      )}
    </div>
  );
}

export default Users;
